import React, { useState, useEffect } from "react";
import { Container, Row, Col, Input, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { API_URL } from "../../../config";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const AddCurrencyList = () => {
  const [previewURL, setPreviewURL] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    currencyName: Yup.string().required("Currency Name is required"),
    currencySymbol: Yup.string().required("Symbol is required"),
    currencySymbolCode: Yup.string().required("Symbol Code is required"),
    currencyType: Yup.string().required("Currency Type is required"),
    basecoin: Yup.string().required("Base Coin is required"),
    decimal: Yup.number()
      .typeError("Must be a number")
      .required("Decimal Point is required"),
    contractAddress: Yup.string().optional(),
    apiid: Yup.string().required("API ID or NA is required"),
    depositEnable: Yup.string().required("Deposit Status is required"),
    withdrawEnable: Yup.string().required("Withdraw Status is required"),
    minWalletTransfer: Yup.number()
      .typeError("Must be a number")
      .required("Minimum Wallet Transfer is required"),

    tagContent: Yup.string().optional(),
    minCurDeposit: Yup.number()
      .typeError("Must be a number")
      .required("Minimum Deposit is required"),
    depositNotes: Yup.string().required("Deposit Notes is required"),
    withdrawNotes: Yup.string().required("Withdraw Notes is required"),
    USDvalue: Yup.number()
      .typeError("Must be a number")
      .required("USD Value is required"),
    status: Yup.string().required("Status is required"),
    siteDecimal: Yup.number()
      .typeError("Must be a number")
      .required("Site Decimal is required"),
    image: Yup.mixed().required("image (file or URL) is required"),
  });

  const submitFormData = async (values, resetForm) => {
    try {
      setLoading(true);
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });
      const resp = await fetch(`${API_URL}/api/wallet/addCurrency`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });
      const data = await resp.json();
      if (data.status === true) {
        toast.success("Currency added successfully!");
        navigate("/currency-list");
        resetForm && resetForm();
        setPreviewURL(null);
      } else {
        alert(data.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      currencyName: "",
      currencySymbol: "",
      currencySymbolCode: "",
      currencyType: "",
      basecoin: "",
      decimal: "",
      contractAddress: "",
      apiid: "",
      depositEnable: "",
      withdrawEnable: "",
      minWalletTransfer: "",

      tagContent: "",
      minCurDeposit: "",
      depositNotes: "",
      withdrawNotes: "",
      USDvalue: "",
      status: "",
      siteDecimal: "",
      image: null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      await submitFormData(values, resetForm);
      setSubmitting(false);
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue("image", file);
      const fileURL = URL.createObjectURL(file);
      setPreviewURL(fileURL);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 font-size-18">Add Currency List</h4>
            </div>
          </Col>
        </Row>
        <form onSubmit={formik.handleSubmit}>
          <div className="grid-2 pt-4 pb-4">
            <div>
              <label>Currency Name</label>
              <Input
                type="text"
                name="currencyName"
                value={formik.values.currencyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Currency Name"
              />
              {formik.touched.currencyName && formik.errors.currencyName && (
                <small className="text-danger">
                  {formik.errors.currencyName}
                </small>
              )}
            </div>

            <div>
              <label>Symbol</label>
              <Input
                type="text"
                name="currencySymbol"
                value={formik.values.currencySymbol}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Symbol"
              />
              {formik.touched.currencySymbol &&
                formik.errors.currencySymbol && (
                  <small className="text-danger">
                    {formik.errors.currencySymbol}
                  </small>
                )}
            </div>

            <div>
              <label>Currency Symbol Code</label>
              <Input
                type="text"
                name="currencySymbolCode"
                value={formik.values.currencySymbolCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Currency Symbol Code"
              />
              {formik.touched.currencySymbolCode &&
                formik.errors.currencySymbolCode && (
                  <small className="text-danger">
                    {formik.errors.currencySymbolCode}
                  </small>
                )}
            </div>

            <div>
              <label>Currency Type (Fiat/Crypto)</label>
              <select
                name="currencyType"
                value={formik.values.currencyType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Currency Type</option>
                <option value="crypto">Crypto</option>
                <option value="fiat">Fiat</option>
              </select>
              {formik.touched.currencyType && formik.errors.currencyType && (
                <small className="text-danger">
                  {formik.errors.currencyType}
                </small>
              )}
            </div>

            <div>
              <label>Base Coin (Select Coin if Not a Token)</label>
              <select
                name="basecoin"
                value={formik.values.basecoin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Base Coin</option>
                <option value="Coin">COIN</option>
                <option value="ERC20">ERC20</option>
                <option value="TRC20">TRC20</option>
                <option value="BEP20">BEP20</option>
                <option value="POL20">POL20</option>
                <option value="SPL">SPL</option>
              </select>
              {formik.touched.basecoin && formik.errors.basecoin && (
                <small className="text-danger">{formik.errors.basecoin}</small>
              )}
            </div>

            <div>
              <label>Decimal Point</label>
              <Input
                type="number"
                style={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "textfield",
                }}
                name="decimal"
                value={formik.values.decimal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Decimal Point"
              />
              {formik.touched.decimal && formik.errors.decimal && (
                <small className="text-danger">{formik.errors.decimal}</small>
              )}
            </div>

            <div>
              <label>Contract Address</label>
              <Input
                type="text"
                name="contractAddress"
                value={formik.values.contractAddress}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Contract Address"
              />
              {formik.touched.contractAddress &&
                formik.errors.contractAddress && (
                  <small className="text-danger">
                    {formik.errors.contractAddress}
                  </small>
                )}
            </div>

            <div>
              <label>
                Coingekko API ID (Enter API ID or NA) Ex: FBT token API ID is
                Exchangepro-exchange
              </label>
              <Input
                type="text"
                name="apiid"
                value={formik.values.apiid}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Coingekko API ID"
              />
              {formik.touched.apiid && formik.errors.apiid && (
                <small className="text-danger">{formik.errors.apiid}</small>
              )}
            </div>

            <div>
              <label>Deposit Status</label>
              <select
                name="depositEnable"
                value={formik.values.depositEnable}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.depositEnable && formik.errors.depositEnable && (
                <small className="text-danger">
                  {formik.errors.depositEnable}
                </small>
              )}
            </div>

            <div>
              <label>Withdraw Status</label>
              <select
                name="withdrawEnable"
                value={formik.values.withdrawEnable}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.withdrawEnable &&
                formik.errors.withdrawEnable && (
                  <small className="text-danger">
                    {formik.errors.withdrawEnable}
                  </small>
                )}
            </div>

            <div>
              <label>Minimum Wallet Transfer</label>
              <Input
                type="number"
                name="minWalletTransfer"
                value={formik.values.minWalletTransfer}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Minimum Wallet Transfer"
              />
              {formik.touched.minWalletTransfer &&
                formik.errors.minWalletTransfer && (
                  <small className="text-danger">
                    {formik.errors.minWalletTransfer}
                  </small>
                )}
            </div>

            <div>
              <label>Deposit Tag Content</label>
              <Input
                type="text"
                name="tagContent"
                value={formik.values.tagContent}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Tag Content"
              />
              {formik.touched.tagContent && formik.errors.tagContent && (
                <small className="text-danger">
                  {formik.errors.tagContent}
                </small>
              )}
            </div>

            <div>
              <label>Minimum Deposit</label>
              <Input
                type="number"
                name="minCurDeposit"
                value={formik.values.minCurDeposit}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Minimum Deposit"
              />
              {formik.touched.minCurDeposit && formik.errors.minCurDeposit && (
                <small className="text-danger">
                  {formik.errors.minCurDeposit}
                </small>
              )}
            </div>

            <div>
              <label>Deposit Notes</label>
              <Input
                type="text"
                name="depositNotes"
                value={formik.values.depositNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Deposit Notes"
              />
              {formik.touched.depositNotes && formik.errors.depositNotes && (
                <small className="text-danger">
                  {formik.errors.depositNotes}
                </small>
              )}
            </div>

            <div>
              <label>Withdraw Notes</label>
              <Input
                type="text"
                name="withdrawNotes"
                value={formik.values.withdrawNotes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Withdraw Notes"
              />
              {formik.touched.withdrawNotes && formik.errors.withdrawNotes && (
                <small className="text-danger">
                  {formik.errors.withdrawNotes}
                </small>
              )}
            </div>

            <div>
              <label>USD Value</label>
              <Input
                type="number"
                name="USDvalue"
                value={formik.values.USDvalue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter USD Value"
              />
              {formik.touched.USDvalue && formik.errors.USDvalue && (
                <small className="text-danger">{formik.errors.USDvalue}</small>
              )}
            </div>

            <div>
              <label>Status</label>
              <select
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-select"
              >
                <option value="">Select Status</option>
                <option value="1">Active</option>
                <option value="0">DeActive</option>
              </select>
              {formik.touched.status && formik.errors.status && (
                <small className="text-danger">{formik.errors.status}</small>
              )}
            </div>

            <div>
              <label>Site Decimal</label>
              <Input
                type="number"
                name="siteDecimal"
                value={formik.values.siteDecimal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="form-control"
                placeholder="Enter Site Decimal"
              />
              {formik.touched.siteDecimal && formik.errors.siteDecimal && (
                <small className="text-danger">
                  {formik.errors.siteDecimal}
                </small>
              )}
            </div>

            <div>
              <label>Image</label>
              <div>
                <Input
                  type="file"
                  className="form-control"
                  onChange={handleFileChange}
                  accept="image/*"
                />

                {previewURL && (
                  <div className="mt-3">
                    <p>Preview:</p>
                    <img
                      src={previewURL}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                )}
              </div>
              {formik.touched.image && formik.errors.image && (
                <small className="text-danger">{formik.errors.image}</small>
              )}
            </div>
          </div>

          <div className="d-r-g-3">
            <button
              className="btn btn-danger btn-sm"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading ? <Spinner size="sm" /> : "Add Currency"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default AddCurrencyList;
